import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '~/utils/trpc'
import { useEffect, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Flex,
  HoverCard,
  Popover,
  ScrollArea,
  Spinner,
  Text
} from '@radix-ui/themes'
import { Command } from 'cmdk'
import { CaretSortIcon, Cross2Icon } from '@radix-ui/react-icons'
import { LuSearch } from 'react-icons/lu'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { useColorFromSeed } from '~/hooks/useColors'

type OrgMember = {
  id: string
  email: string
  role: string
  profile: {
    fullName: string
    avatarURL: string
  }
}

type OrgMemberPickerProps = {
  initialSelection: string[]
  onSelectionChange: (selectedMembers: string[]) => void
  disabled?: boolean
}

const OrgMemberPicker = ({ initialSelection, onSelectionChange, disabled }: OrgMemberPickerProps) => {
  const { t } = useTranslation('settings')
  const trpc = useTRPC()
  const [selectedMembers, setSelectedMembers] = useState<OrgMember[]>([])
  const [open, setOpen] = useState(false)

  const { data: orgMembers = [], isLoading } = useQuery(trpc.orgMembers.listOrgMembers.queryOptions())

  // Initialize selected members from initialSelection prop
  useEffect(() => {
    if (!isLoading && orgMembers.length > 0) {
      const selected = initialSelection
        .map((id) => orgMembers.find((member) => member.id === id))
        .filter((member) => member !== undefined) as OrgMember[]

      setSelectedMembers(selected)
    }
  }, [initialSelection, orgMembers, isLoading])

  // Update parent component when selection changes
  const handleSelectionChange = (members: OrgMember[]) => {
    setSelectedMembers(members)
    onSelectionChange(members.map((member) => member.id))
  }

  // Toggle member selection
  const toggleMember = (member: OrgMember) => {
    const isSelected = selectedMembers.some((m) => m.id === member.id)

    if (isSelected) {
      // Remove member
      handleSelectionChange(selectedMembers.filter((m) => m.id !== member.id))
    } else {
      // Add member
      handleSelectionChange([...selectedMembers, member])
    }
  }

  // Remove a selected member
  const removeMember = (member: OrgMember) => {
    handleSelectionChange(selectedMembers.filter((m) => m.id !== member.id))
  }

  if (isLoading) {
    return (
      <Flex
        align={'center'}
        minHeight={'2.5rem'}
        className={cn('border border-gray-6 hover:bg-gray-2 rounded-md p-1 focus:outline-none', {
          'cursor-not-allowed bg-gray-2': disabled
        })}
      >
        <Spinner ml={'2'} />
      </Flex>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <Popover.Trigger>
        <Flex
          align={'center'}
          minHeight={'2.5rem'}
          className={cn('border border-gray-6 hover:bg-gray-2 rounded-md p-1 focus:outline-none', {
            'cursor-not-allowed bg-gray-2': disabled
          })}
        >
          <Flex pl="1" pr="2" justify={'between'} align="center" height={'100%'} className="w-[100%]">
            {selectedMembers.length > 0 ? (
              <Flex gap="1" wrap="wrap" className="py-[1px]">
                {selectedMembers.map((member) => (
                  <MemberBadge key={member.id} member={member} onRemove={() => removeMember(member)} />
                ))}
              </Flex>
            ) : (
              <Text color="gray" size={'2'}>
                {t('members.selectMembers')}
              </Text>
            )}
            <Box height={'full'} width={'2'} className="ml-auto">
              <CaretSortIcon className="h-4 w-4" />
            </Box>
          </Flex>
        </Flex>
      </Popover.Trigger>
      <Popover.Content className="w-[300px] p-0" align="start">
        <Command className="w-full" loop>
          <Flex align="center" gap="1" p="1" className="border-b border-gray-4">
            <LuSearch className="ml-2 h-4 w-4 text-gray-9" />
            <Command.Input
              autoFocus
              placeholder={t('members.selectMembers')}
              className="text-sm w-full py-2 px-1 outline-none border-none focus:border-none focus:shadow-none focus:ring-0 bg-transparent"
            />
          </Flex>
          <ScrollArea className="max-h-[300px]">
            <Command.List>
              {orgMembers.map((member) => (
                <MemberItem
                  key={member.id}
                  member={member}
                  onSelect={toggleMember}
                  isSelected={selectedMembers.some((m) => m.id === member.id)}
                />
              ))}
            </Command.List>
          </ScrollArea>
        </Command>
      </Popover.Content>
    </Popover.Root>
  )
}

type MemberItemProps = {
  member: OrgMember
  onSelect: (member: OrgMember) => void
  isSelected: boolean
}

const MemberItem = ({ member, onSelect, isSelected }: MemberItemProps) => {
  const avatarColor = useColorFromSeed(member.email)
  return (
    <Command.Item
      key={member.id}
      value={member.email + member.profile.fullName}
      onSelect={() => onSelect(member)}
      className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-3 focus:bg-gray-3 data-[selected=true]:bg-gray-3"
    >
      <Flex align="center" gap="2" width="100%">
        <Checkbox checked={isSelected} onCheckedChange={() => onSelect(member)} className="mr-2" />
        <Avatar
          size="2"
          src={member.profile.avatarURL}
          fallback={member.profile.fullName ? member.profile.fullName[0] : member.email[0]}
          radius="full"
          color={avatarColor}
        />
        <Flex direction="column">
          <Text size="2">{member.profile.fullName || member.email}</Text>
          {member.profile.fullName && (
            <Text size="1" color="gray">
              {member.email}
            </Text>
          )}
        </Flex>
      </Flex>
    </Command.Item>
  )
}

// Badge component for selected members
const MemberBadge = ({ member, onRemove }: { member: OrgMember; onRemove: () => void }) => {
  return (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <Badge size="2" className="flex items-center gap-1">
          <Text>{member.profile.fullName || member.email}</Text>
          <Cross2Icon
            className="h-3 w-3 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          />
        </Badge>
      </HoverCard.Trigger>
      <HoverCard.Content>
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            <Avatar
              size="3"
              src={member.profile.avatarURL}
              fallback={member.profile.fullName ? member.profile.fullName[0] : member.email[0]}
              radius="full"
            />
            <Box>
              {member.profile.fullName && (
                <Text size="2" weight="medium" as="p">
                  {member.profile.fullName}
                </Text>
              )}
              <Text size="2" color="gray">
                {member.email}
              </Text>
            </Box>
          </Flex>
        </Flex>
      </HoverCard.Content>
    </HoverCard.Root>
  )
}

export default OrgMemberPicker
